import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import type { ImportResult } from '../types';

interface ImportResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: ImportResult | null;
}

export function ImportResultModal({ isOpen, onClose, result }: ImportResultModalProps) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    const handleFocusTrap = (e: KeyboardEvent) => {
      if (e.key === 'Tab' && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement?.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement?.focus();
            e.preventDefault();
          }
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('keydown', handleFocusTrap);
      // Focus the close button when modal opens
      const closeButton = modalRef.current?.querySelector('button') as HTMLElement;
      closeButton?.focus();
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleFocusTrap);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !result) return null;

  return (
    <div className="fixed inset-0 z-80 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div 
          ref={modalRef}
          className="relative bg-surface border border-border rounded-xl shadow-lg w-full max-w-md p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="import-result-title"
        >
          <h3 id="import-result-title" className="text-lg font-semibold text-onsurface mb-4">
            {t('translations.import.resultTitle')}
          </h3>

          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-onsurface">{t('translations.import.created')}</span>
              <span className="font-medium text-green-600">{result.created_language_translations}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-onsurface">{t('translations.import.skipped')}</span>
              <span className="font-medium text-yellow-600">{result.skipped}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-border">
              <span className="text-onsurface">{t('translations.import.failed')}</span>
              <span className="font-medium text-red-600">{result.failed}</span>
            </div>
          </div>

          {result.errors && result.errors.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium text-onsurface mb-2">
                {t('translations.import.errors')}
              </p>
              <ul className="text-sm text-red-600 list-disc list-inside max-h-32 overflow-y-auto">
                {result.errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="py-2 px-4 text-sm font-medium rounded-lg bg-primary text-onprimary hover:bg-primary-darker focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
